import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLiff } from '@misterlex223/cloudflare-line-liff-sdk/react';
import { useLiffConfig } from '../../providers/LiffProvider';
import { validateLoginForm } from '../../utils/validation';
import api from '../../services/api';
import LineLoginButton from '../../components/auth/LineLoginButton';
import styles from './MobileLoginForm.module.css';

interface LoginFormData {
  email: string;
  password: string;
}

interface LineAuthData {
  token: string;
  member: {
    id: string;
    email: string;
    name: string;
    storeName: string | null;
    phone: string | null;
    role: string;
    status: string;
    lastLoginAt: string | null;
    createdAt: string;
    updatedAt: string;
  };
  isNewAccount: boolean;
}

const MobileLoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  // LIFF detection for showing LINE login button
  const config = useLiffConfig();
  const [liffInClient, setLiffInClient] = useState(false);

  // Initialize LIFF
  const liff = useLiff({
    ...config,
    onInitSuccess: () => {
      console.log('[MobileLoginForm] LIFF initialized');
      setLiffInClient(liff.isInClient());
    },
    onInitError: () => {
      console.log('[MobileLoginForm] LIFF init failed');
      setLiffInClient(false);
    },
  });

  // Check LIFF state after initialization
  useEffect(() => {
    const checkLiffState = async () => {
      if (!liff.isInitialized) {
        try {
          await liff.init();
        } catch (error) {
          console.error('[MobileLoginForm] LIFF init error:', error);
          setLiffInClient(false);
          return;
        }
      }

      setLiffInClient(liff.isInClient());
    };

    checkLiffState();
  }, [liff]);

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);

  // Check if this is a callback URL
  const isCallbackUrl = useMemo(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    return !!(code && state);
  }, [searchParams]);

  // Stable success callback
  const handleSuccess = useCallback(async (lineResult: any) => {
    console.log('[MobileLoginForm] handleSuccess called with:', lineResult);
    try {
      // 調用 weiwei-car-business API 建立/更新用戶
      console.log('[MobileLoginForm] Calling API /api/auth/line/token...');
      const response = await api.post<LineAuthData>('/api/auth/line/token', {
        lineUserId: lineResult.lineUserId,
        displayName: lineResult.displayName,
        pictureUrl: lineResult.pictureUrl,
        email: lineResult.email,
      });

      console.log('[MobileLoginForm] API response:', response);

      if (response.success && response.data) {
        const { token, requiresRegistration, lineProfile } = response.data as any;

        // Check if registration is required
        if (requiresRegistration && lineProfile) {
          console.log('[MobileLoginForm] Registration required, LINE profile:', lineProfile);
          setIsProcessingCallback(false);
          setSubmitError('請完成註冊流程。您的 LINE 帳號尚未綁定到會員帳號。');
          return;
        }

        if (!token) {
          console.error('[MobileLoginForm] No token in response');
          setSubmitError('無法獲取登入憑證');
          setIsProcessingCallback(false);
          return;
        }

        localStorage.setItem('auth_token', token);
        console.log('[MobileLoginForm] Token saved to localStorage, reloading page to update AuthContext...');

        // Reload page to update AuthContext
        window.location.reload();
      } else {
        console.error('[MobileLoginForm] API failed:', response.error);
        setSubmitError(response.error?.message || 'Authentication failed');
        setIsProcessingCallback(false);
      }
    } catch (err) {
      console.error('[MobileLoginForm] API error:', err);
      setSubmitError(err instanceof Error ? err.message : 'Authentication failed');
      setIsProcessingCallback(false);
    }
  }, [navigate]);

  // Stable error callback
  const handleError = useCallback((err: Error) => {
    setSubmitError(err.message);
    setIsProcessingCallback(false);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateLoginForm(formData.email, formData.password);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        navigate('/member');
      } else {
        setSubmitError(result.error || 'Login failed');
      }
    } catch {
      setSubmitError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // v1.1.0: LIFF auto-login and degrade page handling
  // Show degrade page when in LINE App but LIFF not logged in
  // REMOVED: Auto-login is now only handled by LiffHomePage (/liff route)
  // The login page is for manual login (LINE button or email/password)

  // Show loading state while processing LINE callback
  if (isProcessingCallback) {
    return (
      <div className={styles.container} style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: '1rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #06C755',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: 'var(--mobile-text-secondary)' }}>處理 LINE 登入中...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // In LIFF environment, show only LINE Login button
  if (liffInClient) {
    return (
      <div className={styles.container}>
        <div className={styles.form}>
          <div className={styles.field}>
            <p className={styles.label} style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              使用 LINE 帳號登入
            </p>
          </div>

          {submitError && (
            <div className={styles.error} role="alert">
              {submitError}
            </div>
          )}

          <LineLoginButton
            isInLineClient={liffInClient}
            onSuccess={handleSuccess}
            onError={handleError}
            isCallbackUrl={isCallbackUrl}
            onProcessingChange={setIsProcessingCallback}
            className={styles.lineButton}
          />

          <div className={styles.footer}>
            還不是會員？{' '}
            <a href="/register" className={styles.link}>
              立即註冊
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        {submitError && (
          <div className={styles.error} role="alert">
            {submitError}
          </div>
        )}

        <div className={styles.field}>
          <label htmlFor="email" className={styles.label}>
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
            placeholder="your@email.com"
            disabled={isSubmitting}
            autoComplete="email"
          />
          {errors.email && (
            <span className={styles.fieldError}>{errors.email}</span>
          )}
        </div>

        <div className={styles.field}>
          <label htmlFor="password" className={styles.label}>
            密碼
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
            placeholder="請輸入密碼"
            disabled={isSubmitting}
            autoComplete="current-password"
          />
          {errors.password && (
            <span className={styles.fieldError}>{errors.password}</span>
          )}
        </div>

        <button
          type="submit"
          className={styles.submit}
          disabled={isSubmitting}
        >
          {isSubmitting ? '登入中...' : '登入'}
        </button>

        <div className={styles.divider}>
          <span className={styles.dividerText}>或</span>
        </div>

        <LineLoginButton
          isInLineClient={liffInClient}
          onSuccess={handleSuccess}
          onError={handleError}
          isCallbackUrl={isCallbackUrl}
          onProcessingChange={setIsProcessingCallback}
          className={styles.lineButton}
        />

        <div className={styles.footer}>
          還不是會員？{' '}
          <a href="/register" className={styles.link}>
            立即註冊
          </a>
        </div>
      </form>
    </div>
  );
};

export default MobileLoginForm;
